import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../services/api";
import { API_BASE_URL } from "../services/api";

function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const handleAuthCallback = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const params = new URLSearchParams(location.search);
        const hashParams = new URLSearchParams((window.location.hash || "").replace(/^#/, ""));
        let token = params.get("token");
        const accessToken = hashParams.get("access_token") || params.get("access_token");
        const error = params.get("error");
        const errorDescription = params.get("error_description");

        // --- Gestion des erreurs OAuth ---
        if (error) {
          let message = "Erreur lors de l'authentification.";
          switch (error) {
            case "access_denied":
              message =
                "Accès refusé. Vous avez annulé la demande d'authentification.";
              break;
            case "invalid_grant":
              message =
                "Session expirée ou jeton invalide. Veuillez vous reconnecter.";
              break;
            default:
              message = "Une erreur est survenue pendant la connexion.";
          }

          if (errorDescription) {
            message += ` Détails: ${decodeURIComponent(errorDescription)}`;
          }

          setErrorMessage(message);
          setIsLoading(false);
          return;
        }

        // Flux Google direct (access_token Google) -> échange avec backend
        if (!token && accessToken) {
          const backendResponse = await fetch(`${API_BASE_URL}/auth/google`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ accessToken }),
          });
          const backendResult = await backendResponse.json();
          if (!backendResponse.ok || !backendResult?.success || !backendResult?.token) {
            throw new Error(backendResult?.message || "Échec de la connexion Google côté backend");
          }
          token = backendResult.token;
        }

        // --- Aucun token reçu ---
        if (!token) {
          setErrorMessage(
            "Aucun jeton d'authentification reçu. Veuillez réessayer."
          );
          setIsLoading(false);
          return;
        }

        // --- Stocker le token ---
        localStorage.setItem("token", token);
        
        // --- Attendre brièvement que tout soit prêt ---
        await new Promise((resolve) => setTimeout(resolve, 300));

        // --- Récupération du profil utilisateur ---
        const user = await api.getUserProfile();

        if (user) {
          localStorage.setItem("user", JSON.stringify(user));
          localStorage.setItem("userRole", user.role || "client");
          localStorage.setItem("userType", user.role || "client");

          let redirectPath = "/";
          switch (user.role) {
            case "admin":
              redirectPath = "/admin";
              break;
            case "client":
              redirectPath = "/client";
              break;
            case "demenageur":
              redirectPath = "/demenageur";
              break;
            default:
              redirectPath = "/";
          }

          toast.success("Connexion réussie !");
          navigate(redirectPath, { replace: true });
        } else {
          throw new Error("Impossible de récupérer le profil utilisateur.");
        }
      } catch (err) {
        console.error("Erreur dans le callback d'auth:", err);
        setErrorMessage("Une erreur est survenue. Veuillez réessayer.");
      } finally {
        setIsLoading(false);
      }
    };

    // Lancer le traitement après un léger délai (transition plus fluide)
    const timer = setTimeout(handleAuthCallback, 500);
    return () => clearTimeout(timer);
  }, [location, navigate]);

  // --- Affichage erreur ---
  if (errorMessage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <div className="text-red-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-12 w-12 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Erreur d'authentification
          </h2>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button
            onClick={() => navigate("/login")}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded-md transition duration-200"
          >
            Retour à la page de connexion
          </button>
        </div>
      </div>
    );
  }

  // --- Affichage chargement ---
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          Connexion en cours...
        </h2>
        <p className="text-gray-600">
          Veuillez patienter pendant que nous vous connectons.
        </p>
      </div>
    </div>
  );
}

export default AuthCallback;
